import {
	InspectorControls,
	__experimentalColorGradientSettingsDropdown as ColorGradientSettingsDropdown,
	__experimentalUseMultipleOriginColorsAndGradients as useMultipleOriginColorsAndGradients,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	RangeControl,
	ToggleControl,
	__experimentalUnitControl as UnitControl,
	__experimentalBoxControl as BoxControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';

import { fromColorPreset, toColorPreset } from '../../../utils/helpers';

import {
	TAB_ORIENTATIONS,
	TAB_POSITIONS,
	TAB_ALIGNMENTS,
	LAYOUT_PRESET_OPTIONS,
	CONTENT_ANIMATIONS,
} from '../constants';

export default function Settings({ attributes, setAttributes, clientId }) {
	const {
		layoutPreset,
		orientation,
		tabPosition,
		tabAlignment,
		tabWidth,
		activeTabColor,
		activeTabBg,
		activeTabBgGradient,
		hoverTabColor,
		hoverTabBg,
		hoverTabBgGradient,
		inactiveTabBg,
		inactiveTabBgGradient,
		inactiveTabColor,
		tabGap,
		contentPadding,
		initialTab,
		dimInactiveTabs,
		activateOn,
		contentAnimation,
		mainAnimation,
		sideAnimation,
	} = attributes;

	const hasRegions = 'classic' !== ( layoutPreset || 'classic' );

	const colorGradientSettings = useMultipleOriginColorsAndGradients();

	/*
	 * The dropdown groups swatches by origin (theme, default, custom); the
	 * preset converters want one flat list of { slug, color }.
	 */
	const palette = ( colorGradientSettings.colors || [] ).flatMap(
		( group ) => group.colors || []
	);

	// Detect active block style from WP className
	const activeStyle =
		(attributes.className || '').match(
			/is-style-(\w+)/
		)?.[1] || 'underline';
	// Neither underline style paints the tab itself, so a background control
	// there would set a colour nothing draws.
	const hasBg = ! [ 'underline', 'underline-below' ].includes( activeStyle );

	// ColorGradientControl calls both onColorChange AND onGradientChange when
	// switching between modes (selecting a color calls onGradientChange(undefined)
	// to clear any gradient). Only clear the partner value when actively setting a
	// new one — not on the cleanup undefined call.
	const colorSettings = [
		{
			label: __('Active Tab Color', 'brandy-blocks'),
			colorValue: fromColorPreset( activeTabColor, palette ) || undefined,
			onColorChange: (value) =>
				setAttributes({ activeTabColor: toColorPreset( value, palette ) ?? '' }),
		},
		{
			label: __('Hover Tab Color', 'brandy-blocks'),
			colorValue: fromColorPreset( hoverTabColor, palette ) || undefined,
			onColorChange: (value) =>
				setAttributes({ hoverTabColor: toColorPreset( value, palette ) ?? '' }),
		},
		{
			label: __('Inactive Tab Color', 'brandy-blocks'),
			colorValue: fromColorPreset( inactiveTabColor, palette ) || undefined,
			onColorChange: (value) =>
				setAttributes({ inactiveTabColor: toColorPreset( value, palette ) ?? '' }),
		},
		...(hasBg
			? [
				{
					label: __('Active Tab Background', 'brandy-blocks'),
					colorValue: activeTabBg || undefined,
					gradientValue: activeTabBgGradient || undefined,
					onColorChange: (value) =>
						value
							? setAttributes({
								activeTabBg: value,
								activeTabBgGradient: '',
							})
							: setAttributes({ activeTabBg: '' }),
					onGradientChange: (value) =>
						value
							? setAttributes({
								activeTabBgGradient: value,
								activeTabBg: '',
							})
							: setAttributes({ activeTabBgGradient: '' }),
				},
				{
					label: __('Hover Tab Background', 'brandy-blocks'),
					colorValue: hoverTabBg || undefined,
					gradientValue: hoverTabBgGradient || undefined,
					onColorChange: (value) =>
						value
							? setAttributes({
								hoverTabBg: value,
								hoverTabBgGradient: '',
							})
							: setAttributes({ hoverTabBg: '' }),
					onGradientChange: (value) =>
						value
							? setAttributes({
								hoverTabBgGradient: value,
								hoverTabBg: '',
							})
							: setAttributes({ hoverTabBgGradient: '' }),
				},
				{
					label: __('Inactive Tab Background', 'brandy-blocks'),
					colorValue: inactiveTabBg || undefined,
					gradientValue: inactiveTabBgGradient || undefined,
					onColorChange: (value) =>
						value
							? setAttributes({
								inactiveTabBg: value,
								inactiveTabBgGradient: '',
							})
							: setAttributes({ inactiveTabBg: '' }),
					onGradientChange: (value) =>
						value
							? setAttributes({
								inactiveTabBgGradient: value,
								inactiveTabBg: '',
							})
							: setAttributes({ inactiveTabBgGradient: '' }),
				},
			]
			: []),
	];

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={__('Tab Settings', 'brandy-blocks')}
				>
					<SelectControl
						label={__('Layout', 'brandy-blocks')}
						value={layoutPreset || 'classic'}
						options={LAYOUT_PRESET_OPTIONS}
						onChange={(value) =>
							setAttributes({ layoutPreset: value })
						}
						help={__(
							'Side layouts split each tab into two areas. Switching back to Classic merges them into one.',
							'brandy-blocks'
						)}
					/>
					<SelectControl
						label={__('Orientation', 'brandy-blocks')}
						value={orientation}
						options={TAB_ORIENTATIONS}
						onChange={(value) =>
							setAttributes({
								orientation: value,
								tabPosition:
									value === 'vertical' ? 'left' : 'top',
							})
						}
					/>
					<SelectControl
						label={__('Position', 'brandy-blocks')}
						value={tabPosition}
						options={TAB_POSITIONS[orientation]}
						onChange={(value) =>
							setAttributes({ tabPosition: value })
						}
					/>
					<SelectControl
						label={__('Alignment', 'brandy-blocks')}
						value={tabAlignment}
						options={TAB_ALIGNMENTS}
						onChange={(value) =>
							setAttributes({ tabAlignment: value })
						}
					/>
					{/*
					 * Hover activation exists so a tab can carry a link: once
					 * clicking a tab navigates, hovering is what is left to
					 * change panels. Focus activates too, otherwise the panels
					 * would be unreachable without a mouse.
					 */}
					<SelectControl
						label={__('Activate tab on', 'brandy-blocks')}
						value={activateOn || 'click'}
						options={[
							{ label: __('Click', 'brandy-blocks'), value: 'click' },
							{ label: __('Hover', 'brandy-blocks'), value: 'hover' },
						]}
						onChange={(value) =>
							setAttributes({ activateOn: value })
						}
						help={__(
							'Choose Hover when tabs link somewhere, so clicking can follow the link. Touch devices have no hover, so they stay on the initial tab.',
							'brandy-blocks'
						)}
					/>
					{/*
					 * Strip-wide on purpose: dimming half a nav row would read as
					 * a mistake, so this is one decision for every tab rather
					 * than a per-tab attribute.
					 */}
					<ToggleControl
						label={__('Dim inactive tabs', 'brandy-blocks')}
						checked={!!dimInactiveTabs}
						onChange={(value) =>
							setAttributes({ dimInactiveTabs: value })
						}
						help={__(
							'Fades unselected tabs — logo, icon and label together — so the selected one stands out.',
							'brandy-blocks'
						)}
					/>
					<RangeControl
						label={__(
							'Initial Active Tab',
							'brandy-blocks'
						)}
						value={initialTab}
						onChange={(value) =>
							setAttributes({ initialTab: value })
						}
						min={1}
						max={10}
						step={1}
						help={__(
							'1 = first tab, 2 = second, etc.',
							'brandy-blocks'
						)}
					/>
					{orientation === 'vertical' && (
						<UnitControl
							label={__('Tab Width', 'brandy-blocks')}
							value={tabWidth}
							onChange={(value) =>
								setAttributes({ tabWidth: value })
							}
							units={[
								{ value: 'px', label: 'px', default: 150 },
								{ value: '%', label: '%', default: 20 },
								{ value: 'rem', label: 'rem', default: 10 },
							]}
						/>
					)}
					<UnitControl
						label={__('Tab Gap', 'brandy-blocks')}
						value={tabGap}
						onChange={(value) =>
							setAttributes({ tabGap: value })
						}
						units={[
							{ value: 'px', label: 'px', default: 0 },
							{ value: 'rem', label: 'rem', default: 0 },
						]}
					/>
				</PanelBody>
				<PanelBody
					title={__('Animation', 'brandy-blocks')}
					initialOpen={false}
				>
					{hasRegions ? (
						<>
							<SelectControl
								label={__('Main Area', 'brandy-blocks')}
								value={mainAnimation}
								options={CONTENT_ANIMATIONS}
								onChange={(value) =>
									setAttributes({ mainAnimation: value })
								}
							/>
							<SelectControl
								label={__('Side Area', 'brandy-blocks')}
								value={sideAnimation}
								options={CONTENT_ANIMATIONS}
								onChange={(value) =>
									setAttributes({ sideAnimation: value })
								}
								help={__(
									'The area in the leading column enters first; the other follows just behind it.',
									'brandy-blocks'
								)}
							/>
						</>
					) : (
						<SelectControl
							label={__('Content', 'brandy-blocks')}
							value={contentAnimation}
							options={CONTENT_ANIMATIONS}
							onChange={(value) =>
								setAttributes({ contentAnimation: value })
							}
							help={__(
								'How panel content enters when a tab is opened. The outgoing panel fades out beneath it.',
								'brandy-blocks'
							)}
						/>
					)}
				</PanelBody>
				<PanelBody
					title={__('Content', 'brandy-blocks')}
					initialOpen={false}
				>
					<BoxControl
						label={__(
							'Content Padding',
							'brandy-blocks'
						)}
						values={contentPadding}
						onChange={(value) =>
							setAttributes({ contentPadding: value })
						}
					/>
				</PanelBody>
			</InspectorControls>

			<InspectorControls group="color">
				<ColorGradientSettingsDropdown
					__experimentalIsRenderedInSidebar
					settings={colorSettings}
					panelId={clientId}
					{...colorGradientSettings}
				/>
			</InspectorControls>
		</>
	);
}